// MyBoxEnhanced.tsx
import React, { useState } from "react";

interface MyBoxProps {
  className?: string;
  children?: React.ReactNode;
  topText?: React.ReactNode;
  topInfo?: string;
  bottomInfo?: string;
  required?: boolean;
  variant?: 'default' | 'outlined' | 'filled' | 'elevated' | 'gradient';
  padding?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  shadow?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  border?: boolean;
  borderColor?: string;
  hoverable?: boolean;
  clickable?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  skeleton?: boolean;
  gradientFrom?: string;
  gradientTo?: string;
}

const MyBox: React.FC<MyBoxProps> = ({
  className = "",
  children,
  topText,
  topInfo,
  bottomInfo,
  required,
  variant = 'default',
  padding = 'md',
  shadow = 'md',
  border = true,
  borderColor,
  hoverable = false,
  clickable = false,
  onClick,
  disabled = false,
  loading = false,
  skeleton = false,
  gradientFrom = 'from-blue-500',
  gradientTo = 'to-purple-600',
}) => {
  const [isHovered, setIsHovered] = useState(false);

  // Variant styles
  const variantStyles = {
    default: 'bg-white dark:bg-gray-800',
    outlined: 'bg-transparent dark:bg-transparent',
    filled: 'bg-gray-50 dark:bg-gray-800/50',
    elevated: 'bg-white dark:bg-gray-800',
    gradient: `bg-gradient-to-br ${gradientFrom} ${gradientTo} text-white`,
  };

  // Padding styles
  const paddingStyles = {
    none: 'p-0',
    sm: 'p-2',
    md: 'p-3 sm:p-4',
    lg: 'p-4 sm:p-5',
    xl: 'p-5 sm:p-6',
  };

  // Shadow styles
  const shadowStyles = {
    none: '',
    sm: 'shadow-sm dark:shadow-gray-900/20',
    md: 'shadow-md dark:shadow-gray-900/30',
    lg: 'shadow-lg dark:shadow-gray-900/40',
    xl: 'shadow-xl dark:shadow-gray-900/50',
  };

  // Border styles
  const getBorderStyles = () => {
    if (!border) return '';
    if (borderColor) return `border ${borderColor}`;
    return 'border border-gray-200 dark:border-gray-700';
  };

  // Hover styles
  const getHoverStyles = () => {
    if (disabled) return '';
    if (hoverable) {
      return 'transition-all duration-300 hover:shadow-lg dark:hover:shadow-gray-900/40 hover:border-gray-300 dark:hover:border-gray-600 hover:-translate-y-0.5';
    }
    if (clickable) {
      return 'cursor-pointer transition-all duration-200 hover:shadow-md dark:hover:shadow-gray-900/30 active:scale-98';
    }
    return '';
  };

  // Top padding adjustment based on topText
  const topPadding = topText ? 'pt-6 sm:pt-7' : 'pt-0';

  // Disabled styles
  const disabledStyles = disabled ? 'opacity-60 cursor-not-allowed' : '';

  // Loading skeleton
  if (skeleton) {
    return (
      <div className={`
        relative w-full flex flex-col rounded-xl
        ${variantStyles.default}
        ${getBorderStyles()}
        ${shadowStyles[shadow]}
        ${paddingStyles[padding]}
        ${topPadding}
        animate-pulse
      `}>
        {topText && (
          <div className="absolute -top-3 left-4 z-10">
            <div className="h-5 w-24 bg-gray-200 dark:bg-gray-700 rounded-full" />
          </div>
        )}
       
        <div className="space-y-3">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6" />
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className={`
        relative w-full flex flex-col rounded-xl
        ${variantStyles[variant]}
        ${getBorderStyles()}
        ${shadowStyles[shadow]}
        ${paddingStyles[padding]}
        ${topPadding}
        ${disabledStyles}
      `}>
        {topText && (
          <div className="absolute -top-3 left-4 z-10">
            <div className="bg-white dark:bg-gray-800 px-2.5 py-1 rounded-full border border-gray-200 dark:border-gray-600 shadow-sm">
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-700 dark:text-gray-300">{topText}</span>
                {required && <span className="text-red-500 text-xs">*</span>}
              </div>
            </div>
          </div>
        )}
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 dark:border-blue-400" />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`
        relative 
        w-full flex flex-col
        rounded-xl
        ${variantStyles[variant]}
        ${getBorderStyles()}
        ${shadowStyles[shadow]}
        ${paddingStyles[padding]}
        ${topPadding}
        space-y-3
        overflow-visible
        ${getHoverStyles()}
        ${disabledStyles}
        transition-all duration-200
        ${className}
      `}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => {
        if (clickable && !disabled && onClick) {
          onClick();
        }
      }}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable && !disabled ? 0 : undefined}
      onKeyDown={(e) => {
        if (clickable && !disabled && (e.key === 'Enter' || e.key === ' ') && onClick) {
          onClick();
        }
      }}
    >
      {/* TOP LABEL */}
      {topText && (
        <div
          className={`
            absolute -top-3 left-4
            z-10
            text-[11px] sm:text-xs
            bg-white dark:bg-gray-800
            text-gray-700 dark:text-gray-300
            px-2.5 py-1 
            rounded-full
            border border-gray-200 dark:border-gray-600
            pointer-events-none
            shadow-sm
            font-medium
            transition-all duration-200
            ${isHovered && hoverable ? 'shadow-md -translate-y-0.5' : ''}
          `}
        >
          <div className="flex items-center gap-1.5">
            <span className="truncate max-w-[200px] sm:max-w-none">
              {topText}
            </span>
            {required && (
              <span className="text-red-500 dark:text-red-400 text-xs font-bold">*</span>
            )}
          </div>
        </div>
      )}




      {/* CONTENT */}
      <div className={`w-full ${topText ? "pt-3" : "pt-2"}`}>
         {topInfo && (
          <div className="">
            <div className=" w-full text-amber-300 font-light text-[12px] pb-1" >{topInfo}</div>
          </div>
        )}
        {children}
         {bottomInfo && (
          <div className="">
            <div className=" w-full text-amber-300 font-light text-[12px] pt-1" >{bottomInfo}</div>
          </div>
        )}
      </div>

      {/* Optional: Bottom gradient line for gradient variant */}
      {variant === 'gradient' && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-white/20 to-transparent rounded-b-xl" />
      )}
    </div>
  );
};

export default MyBox;